import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { AccountActions, AccountSelectors, AuthorizationActions, AuthorizationSelectors, CoreSelectors, ModulesSelectors, NavigationSelectors, TrackingActions, ValidationsService } from '@serviceos-ng/core';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
    SearchCountryField,
    CountryISO,
    PhoneNumberFormat,
} from 'ngx-intl-tel-input';

interface PhonesComponentConfig {
    title: string;
    empty_message: string;
    delete_button_title: string;
    add_button_title: string;
    add_form: {
        title: string;
        phone_input_placeholder: {
            gb: string;
            ie: string;
            au: string;
            hun: string;
            us: string;
        };
        phone_input_error: string;
        confirm_button_title: string;
    };
}

export interface FormData {
    type_id: number;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    birth_date_formatted?: any;
    phone: any;
    referrer_code?: string;
    register_token?: string;
    preferences?: {
        allow_mk_email: boolean,
        allow_mk_sms: boolean,
    };
    external?: boolean;
    api_version?: string;
    language?: string;
  }

export function phoneValidator(regex) {
    return (c: AbstractControl) => {
        return c.value && regex.test(c.value.replace(/\s/g, ''))
            ? null
            : { pattern: true };
    };
}

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'serviceos-web-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss', './../../../../../../../node_modules/intl-tel-input/build/css/intlTelInput.css'],
})
export class RegisterComponent implements OnInit, OnDestroy {
    @Select(AuthorizationSelectors.getSocialProviders)
    public socialProviders$!: Observable<any>;

    @Select(ModulesSelectors.getSetting('terms_and_conditions_url'))
    terms_and_conditions_url$;

    @Select(ModulesSelectors.getSetting('privacy_policy_url'))
    privacy_policy_url$;

    @Select(ModulesSelectors.getSetting('country_code'))
    country_code$!: Observable<CountryISO>;

    @Select(CoreSelectors.brandName)
    brandName$!: Observable<string>;

    @Select(ModulesSelectors.getModuleConfigText('auth'))
    moduleAuthTexts$!: Observable<any>;

    @Output()
    public changeScreenEvent = new EventEmitter();

    @Input()
    public referralParamCode: any = '';

    selectedCountryICO = CountryISO.UnitedKingdom;

    phoneConfig: PhonesComponentConfig | null = null;

    public subscriptions: Subscription[] = [];

    public isEventSent = false;

    public isReferralEnabled = false;

    public registerForm: FormGroup;
    public termsAndConditionsUrl = '';
    public privacyPolicyUrl = '';
    public formErrorMessage = '';
    public formSubmitted = false;
    public showReferral = false;
    public birthdateFormFieldEnabled = false;
    public birthDateHasError = false;
    public isFormExpand = false;
    public showLocalLoader = false;

    public separateDialCode = true;
    public SearchCountryField = SearchCountryField;
	public CountryISO = CountryISO;
    public PhoneNumberFormat = PhoneNumberFormat;
	public preferredCountries: CountryISO[] = [];
    public selectedCountry= CountryISO[this.getPreferredCountriesKeys(['UK'])[0]];
    public customPlaceholder = '';
    public registerToken = '';

    public emailFormExpand = false;
    public socialProviders: any = [];
    public socialsProvidersInit = false;

    public authModuleTexts: any;

    @ViewChild('birthDateField') public birthDateField: any;

    /**
     * Basic component settings
     */
    public componentSettings = {
        // eslint-disable-next-line @typescript-eslint/quotes
        msg:
            "You're nearly there! Before we can continue, please check the information you have provided in these fields:",
        errorMessages: [],
        details: {
            title: 'Your details',
            fields: {
                first_name: {
                    label: 'First Name',
                    placeholder: '',
                    errMsg: 'Please enter your first name.',
                },
                last_name: {
                    label: 'Last Name',
                    placeholder: '',
                    errMsg: 'Please enter your last name.',
                },
                birth_date: {
                    label: 'Birthdate',
                    placeholder: '"YYYY-MM-DD',
                    errMsg:
                        'Please enter a valid birthdate format (e.g. 1980-12-30)',
                },
                phone: {
                    label: 'Phone',
                    placeholder: '',
                    customPlaceholder: 'custom placeholder',
                    errMsg: 'Please enter your phone.',
                    icon: '',
                },
                email: {
                    label: 'Email',
                    placeholder: '',
                    errMsgs: {
                        required: 'Please enter your email address.',
                        format: 'Email is invalid',
                    },
                    icon: '',
                    // icon: 'envelope-o'
                },
                password: {
                    label: 'Password',
                    placeholder: '',
                    errMsgs: {
                        required: 'Please enter a valid password',
                        format:
                            'Your password must be at least 5 characters long.',
                    },
                    errMsg: 'Please enter a valid password',
                    icon: '',
                    showPassword: false,
                },
                confirmPassword: {
                    label: 'Repeat password',
                    placeholder: '',
                    errMsg: 'Password mismatch.',
                    icon: '',
                    showPassword: false,
                },
                address: {
                    label: 'Address line 1',
                    placeholder: '',
                    icon: '',
                },
                postcode: {
                    label: 'Postcode',
                    placeholder: 'SE1 2TH',
                    icon: '',
                    note:
                        'The postcode cannot be changed because we use it to find the nearest professional and calculate the service price.',
                },
                terms: {
                    label: '', // in template
                    errMsg: 'This field is required.',
                },
                smsOptIn: {
                    label: 'SMS',
                },
                emailOptIn: {
                    label: 'Email',
                },
                referralCodeField: {
                    label: 'Referral code',
                    placeholder: 'e.g., john8D37',
                    icon: '',
                    errMsg: 'Invalid referral code.',
                },
                signUp: {
                    label: 'Sign up',
                },
            },
            labels: {
                signUp: {
                    label: 'or',
                },
            },
        },
        address: {
            title: 'Property',
        },
        payment: {
            title: 'Payment',
        },
    };

    public sliderData: any = [];

    constructor(
        private fb: FormBuilder,
        private _validationService: ValidationsService,
        private _store: Store
    ) {
        

        const sliderConfig = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule(
            'auth',
            'slider'
        ));

        const navigationMenu = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule('account', 'menu'));
        this.isReferralEnabled = navigationMenu?.items.some(item => item.name === 'referral');

        if (sliderConfig?.length) {
            sliderConfig.filter(slide => {
                if (slide?.type === 'benefit') {
                    slide.benefits.map(benefit => {
                        this.sliderData.push({
                            type: 'text',
                            description: benefit.description
                        })
                    })
                }
            })
        }
    
        this.registerForm = this.fb.group({
            first_name: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.pattern(
                        this._validationService.getPattern(
                            'register',
                            'first_name'
                        )
                    ),
                ],
            ],
            last_name: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.pattern(
                        this._validationService.getPattern(
                            'register',
                            'last_name'
                        )
                    ),
                ],
            ],
            email: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        this._validationService.getPattern('register', 'email')
                    ),
                ],
            ],
            phone: new FormControl(undefined, [Validators.required]),
            passwordGroup: this.fb.group(
                {
                    password: [
                        '',
                        [
                            Validators.required,
                            Validators.minLength(5),
                            Validators.pattern(
                                this._validationService.getPattern(
                                    'register',
                                    'password'
                                )
                            ),
                        ],
                    ],
                    // confirmPassword: ['', Validators.required] // * comment because changes with task #38345
                }
                // { validator: passwordMatcher }// * comment because changes with task #38345
            ),
            referralCodeField: [
                '',
                [
                    Validators.pattern(
                        this._validationService.getPattern(
                            'register',
                            'referrer_code'
                        )
                    ),
                ],
            ],
            terms: ['', Validators.requiredTrue],
            smsOptIn: [''],
            emailOptIn: [''],
        });        
    }

    /**
     * Geter for field in registation form
     */
    get first_name() {
        return this.registerForm.get('first_name');
    }

    /**
     * Geter for field in registation form
     */
    get last_name() {
        return this.registerForm.get('last_name');
    }

    /**
     * Geter for field in registation form
     */
    get birth_date() {
        return this.registerForm.get('birth_date');
    }

    /**
     * Geter for field in registation form
     */
    get email() {
        return this.registerForm.get('email');
    }

    /**
     * Geter for field in registation form
     */
    get phone() {
        return this.registerForm.get('phone');
    }

    /**
     * Geter for field in registation form
     */
    get passwordGroup() {
        return this.registerForm.get('passwordGroup');
    }

    /**
     * Geter for field in registation form
     */
    get referralCode() {
        return this.registerForm.get('referralCode');
    }

    /**
     * Geter for field in registation form
     */
    get referralCodeField() {
        return this.registerForm.get('referralCodeField');
    }

    /**
     * Geter for field in registation form
     */
    get password() {
        return this.registerForm.get('passwordGroup')?.get('password');
    }

    get terms() {
        return this.registerForm.get('terms');
    }

    get emailOptIn() {
        return this.registerForm.get('emailOptIn');
    }

    get smsOptIn() {
        return this.registerForm.get('smsOptIn');
    }

    ngOnInit(): void {

        this.authModuleTexts = this._store.selectSnapshot(ModulesSelectors.getModuleConfigText('auth'));

        const disableAuthRegisterOptions = this._store.selectSnapshot(
            ModulesSelectors.getSetting('disable_auth_register_options')
        );

        // Prefill referral code
        if (this.referralParamCode) {
            this.showReferral = true;

            this.registerForm.patchValue({
                referralCodeField: this.referralParamCode
            });
        }

        const initRouteData = this._store.selectSnapshot(NavigationSelectors.getInitRoute);

        if (initRouteData?.data?.token) {
            this.registerToken = initRouteData.data.token;
        }

        this.socialProviders = this._store.selectSnapshot(AuthorizationSelectors.getSocialProviders);

        // filter social providers
        if (disableAuthRegisterOptions) {
            this.socialProviders = this.socialProviders.filter((item) => disableAuthRegisterOptions?.indexOf(item.type?.toLowerCase()) === -1);
        }

        if (!this.socialProviders || !(this.socialProviders && this.socialProviders.length > 0)) {
            const sp = this._store.dispatch(AuthorizationActions.GetSocialProviders)
            .subscribe(
                (data) => {
                    this.socialProviders = this._store.selectSnapshot(AuthorizationSelectors.getSocialProviders);

                    // filter social providers
                    if (disableAuthRegisterOptions) {
                        this.socialProviders = this.socialProviders.filter((item) => disableAuthRegisterOptions?.indexOf(item.type?.toLowerCase()) === -1);
                    }

                    this.socialsProvidersInit = true;
                    this.isFormExpand = true;
                }
            )
        } else {
            this.socialsProvidersInit = true;
        }
        
        this.subscriptions.push(
            this.country_code$.subscribe((country: any) => {
                const countryCode = country;
                const preferredCountries: string[] = ['UK','AUS','US'];

                // Set default country flag based on configuration
                if (countryCode) {
                    this.selectedCountry = CountryISO[this.getPreferredCountriesKeys([countryCode])[0]];
                } else {
                    this.selectedCountry = CountryISO[this.getPreferredCountriesKeys(['UK'])[0]];
                }

                // Set preferred countries
                this.getPreferredCountriesKeys(preferredCountries).map(countryKey => {
                    this.preferredCountries.push(CountryISO[countryKey]);
                });

                if (typeof country == 'string') {
                    const countries = Object.values(CountryISO);
                    country = country.toLocaleLowerCase();
                    if (countries.includes(country)) {
                        this.countryChange(country);
                        this.selectedCountryICO = country;
                    }

                }
            })
        );
    }

    // Check form email login option in social provers
    public hasEmailSocialLogin(): boolean {
        // Remove this checker when email with login comes only from social provders !!!
        const emailOption = this.socialProviders?.length ? this.socialProviders.filter((provider) => provider.type === 'Email') : [];

        return !!emailOption.length;
    }

    public countryChange(countryIso: string) {
        if (this.phoneConfig?.add_form?.phone_input_placeholder[countryIso]) {
            this.customPlaceholder = this.phoneConfig?.add_form?.phone_input_placeholder[
                countryIso
            ];
        } else {
            this.customPlaceholder = '';
        }
    }

    public loginSocial(data) {

        this.showLocalLoader = true;

        this._store
            .dispatch(new AuthorizationActions.SocialSingIn(data))
            .pipe(
                finalize(
                    ()=> {
                        this.showLocalLoader = false;
                    }
                )
            )
            .subscribe(
                (response) => {
                    this.formErrorMessage = '';
                    this.getAuthorizeUserData(data);
                },
                (errorResponse) => {

                    this.formErrorMessage = errorResponse.error
                        ? errorResponse?.error[0]?.message
                        : this.authModuleTexts?.error_social_authorization_fail ? this.authModuleTexts.error_social_authorization_fail : 'Unable to sign up. Please try again later or use a different method to sign up.';
                    
                    setTimeout(() => {
                        try {
                            const modalElement: HTMLElement | null = document.querySelector('.authentication-modal-box');

                            if (modalElement) modalElement.scrollIntoView({ behavior: 'smooth' });
                        } catch (err) {
                            // selector didnt work
                        }
                    }, 1)
                }
            );
    }

    public validateForm() {
        // old email login
        if (!this.isFormExpand && !this.hasEmailSocialLogin()) {
            this.isFormExpand = true;
            return;
        }

        // new email login
        if (!this.emailFormExpand && this.hasEmailSocialLogin()) {
            this.emailFormExpand = true;
            return;
        }

        this.formSubmitted = true;        

        if (this.registerForm.valid) {
            const formValue = this.registerForm.value;

            const formData: FormData = {
                type_id: 2, // 1 - Anonymous | 2 - Generic (register form) | 3 - Social | 4 - Offline
                email: formValue.email,
                password: formValue.passwordGroup.password,
                register_token: this.registerToken ? this.registerToken : '',
                first_name: formValue.first_name,
                last_name: formValue.last_name,
                birth_date_formatted:
                    this.birth_date && this.birthDateField.nativeElement.value
                        ? this.birthDateField.nativeElement.value
                        : null,
                phone: formValue.phone.e164Number,
                referrer_code: formValue.referralCodeField,
                external: true,
                api_version: 'v2'
            };

            if (formValue.emailOptIn || formValue.smsOptIn) {
                formData.preferences = {
                    allow_mk_email: formValue.emailOptIn ? true : false,
                    allow_mk_sms: formValue.smsOptIn ? true : false,
                }
            }

            this.showLocalLoader = true;

            this._store
            .dispatch(new AuthorizationActions.Register(formData))
            .pipe(
                finalize(() => {
                    // this.loginRequestInProcess = false;
                    this.showLocalLoader = false;
                })
            )
            .subscribe(
                (response) => {
                    this.formErrorMessage = '';
                    this.getAuthorizeUserData({type: 'standard'});
                },
                (errorResponse) => {
                    this.formErrorMessage = errorResponse.error
                        ? errorResponse?.error[0]?.message
                        : 'Unable to sign in. Please try again later or use a different method to sign in.';
                        
                    setTimeout(() => {
                        try {
                            const modalElement: HTMLElement | null = document.querySelector('.authentication-modal-box');

                            if (modalElement) modalElement.scrollIntoView({ behavior: 'smooth' });
                        } catch (err) {
                            // selector didnt work
                        }
                    }, 1)
                }
            );
        } else {
            // this.DataLayerPush('error');
            // scrollToTop('.obf-lr-modal');
        }
    }

    private getAuthorizeUserData(loginData?) {
        this.showLocalLoader = true;
        this._store.dispatch(new AccountActions.Fetch())
        .pipe(
            finalize(
                () => {
                    this.showLocalLoader = false;
                }
            )
        )
        .subscribe(
            (success) => {
                this.subscriptions.push(this._store.select(AccountSelectors.account)
                    .subscribe(
                        (data) => {

                            const eventData = {
                                type: loginData?.type,
                                membership: data?.membership,
                                user_id: data?.id
                            }
                    
                            if (!this.isEventSent && data) {
                                this._store.dispatch(
                                    new TrackingActions.Push('sign_up', eventData)
                                );

                                this.isEventSent = true;
                            }
                        }
                    ));
            }
        );
    }

    public changeScreen(screen) {
        this.changeScreenEvent.emit(screen);
    }

    public getPreferredCountriesKeys(countries: string[]): string[] {
        const countriesKeys: string[] = [];
    
        if (countries && countries.length) {
            countries.map((country) => {
                let countryKey = '';
                const countryUpperKeys = country.toUpperCase();
                const isISO3 = countryUpperKeys.length === 3 ? true : false;
                let regionNames;
    
                countryKey = isISO3 ? this.getCountryISO2(countryUpperKeys) : countryUpperKeys;
                
                // countriesKeys remains empty array for old browsers which do not support new Intl['DisplayNames']
                try {
                    regionNames = new Intl['DisplayNames'](['en'], {type: 'region'});
                    countriesKeys.push(regionNames.of(countryKey).replace(/ /g,''));
                } catch(e) {
                }
            });
        }
    
        return countriesKeys;
    }

    public getCountryISO2(countryCode) {
    const countryISOMapping = {
        AFG: "AF",
        ALA: "AX",
        ALB: "AL",
        DZA: "DZ",
        ASM: "AS",
        AND: "AD",
        AGO: "AO",
        AIA: "AI",
        ATA: "AQ",
        ATG: "AG",
        ARG: "AR",
        ARM: "AM",
        ABW: "AW",
        AUS: "AU",
        AUT: "AT",
        AZE: "AZ",
        BHS: "BS",
        BHR: "BH",
        BGD: "BD",
        BRB: "BB",
        BLR: "BY",
        BEL: "BE",
        BLZ: "BZ",
        BEN: "BJ",
        BMU: "BM",
        BTN: "BT",
        BOL: "BO",
        BES: "BQ",
        BIH: "BA",
        BWA: "BW",
        BVT: "BV",
        BRA: "BR",
        VGB: "VG",
        IOT: "IO",
        BRN: "BN",
        BGR: "BG",
        BFA: "BF",
        BDI: "BI",
        KHM: "KH",
        CMR: "CM",
        CAN: "CA",
        CPV: "CV",
        CYM: "KY",
        CAF: "CF",
        TCD: "TD",
        CHL: "CL",
        CHN: "CN",
        HKG: "HK",
        MAC: "MO",
        CXR: "CX",
        CCK: "CC",
        COL: "CO",
        COM: "KM",
        COG: "CG",
        COD: "CD",
        COK: "CK",
        CRI: "CR",
        CIV: "CI",
        HRV: "HR",
        CUB: "CU",
        CUW: "CW",
        CYP: "CY",
        CZE: "CZ",
        DNK: "DK",
        DJI: "DJ",
        DMA: "DM",
        DOM: "DO",
        ECU: "EC",
        EGY: "EG",
        SLV: "SV",
        GNQ: "GQ",
        ERI: "ER",
        EST: "EE",
        ETH: "ET",
        FLK: "FK",
        FRO: "FO",
        FJI: "FJ",
        FIN: "FI",
        FRA: "FR",
        GUF: "GF",
        PYF: "PF",
        ATF: "TF",
        GAB: "GA",
        GMB: "GM",
        GEO: "GE",
        DEU: "DE",
        GHA: "GH",
        GIB: "GI",
        GRC: "GR",
        GRL: "GL",
        GRD: "GD",
        GLP: "GP",
        GUM: "GU",
        GTM: "GT",
        GGY: "GG",
        GIN: "GN",
        GNB: "GW",
        GUY: "GY",
        HTI: "HT",
        HMD: "HM",
        VAT: "VA",
        HND: "HN",
        HUN: "HU",
        ISL: "IS",
        IND: "IN",
        IDN: "ID",
        IRN: "IR",
        IRQ: "IQ",
        IRL: "IE",
        IMN: "IM",
        ISR: "IL",
        ITA: "IT",
        JAM: "JM",
        JPN: "JP",
        JEY: "JE",
        JOR: "JO",
        KAZ: "KZ",
        KEN: "KE",
        KIR: "KI",
        PRK: "KP",
        KOR: "KR",
        KWT: "KW",
        KGZ: "KG",
        LAO: "LA",
        LVA: "LV",
        LBN: "LB",
        LSO: "LS",
        LBR: "LR",
        LBY: "LY",
        LIE: "LI",
        LTU: "LT",
        LUX: "LU",
        MKD: "MK",
        MDG: "MG",
        MWI: "MW",
        MYS: "MY",
        MDV: "MV",
        MLI: "ML",
        MLT: "MT",
        MHL: "MH",
        MTQ: "MQ",
        MRT: "MR",
        MUS: "MU",
        MYT: "YT",
        MEX: "MX",
        FSM: "FM",
        MDA: "MD",
        MCO: "MC",
        MNG: "MN",
        MNE: "ME",
        MSR: "MS",
        MAR: "MA",
        MOZ: "MZ",
        MMR: "MM",
        NAM: "NA",
        NRU: "NR",
        NPL: "NP",
        NLD: "NL",
        ANT: "AN",
        NCL: "NC",
        NZL: "NZ",
        NIC: "NI",
        NER: "NE",
        NGA: "NG",
        NIU: "NU",
        NFK: "NF",
        MNP: "MP",
        NOR: "NO",
        OMN: "OM",
        PAK: "PK",
        PLW: "PW",
        PSE: "PS",
        PAN: "PA",
        PNG: "PG",
        PRY: "PY",
        PER: "PE",
        PHL: "PH",
        PCN: "PN",
        POL: "PL",
        PRT: "PT",
        PRI: "PR",
        QAT: "QA",
        REU: "RE",
        ROU: "RO",
        RUS: "RU",
        RWA: "RW",
        BLM: "BL",
        SHN: "SH",
        KNA: "KN",
        LCA: "LC",
        MAF: "MF",
        SPM: "PM",
        VCT: "VC",
        WSM: "WS",
        SMR: "SM",
        STP: "ST",
        SAU: "SA",
        SEN: "SN",
        SRB: "RS",
        SYC: "SC",
        SLE: "SL",
        SGP: "SG",
        SXM: "SX",
        SVK: "SK",
        SVN: "SI",
        SLB: "SB",
        SOM: "SO",
        ZAF: "ZA",
        SGS: "GS",
        SSD: "SS",
        ESP: "ES",
        LKA: "LK",
        SDN: "SD",
        SUR: "SR",
        SJM: "SJ",
        SWZ: "SZ",
        SWE: "SE",
        CHE: "CH",
        SYR: "SY",
        TWN: "TW",
        TJK: "TJ",
        TZA: "TZ",
        THA: "TH",
        TLS: "TL",
        TGO: "TG",
        TKL: "TK",
        TON: "TO",
        TTO: "TT",
        TUN: "TN",
        TUR: "TR",
        TKM: "TM",
        TCA: "TC",
        TUV: "TV",
        UGA: "UG",
        UKR: "UA",
        ARE: "AE",
        GBR: "GB",
        USA: "US",
        UMI: "UM",
        URY: "UY",
        UZB: "UZ",
        VUT: "VU",
        VEN: "VE",
        VNM: "VN",
        VIR: "VI",
        WLF: "WF",
        ESH: "EH",
        YEM: "YE",
        ZMB: "ZM",
        ZWE: "ZW",
        XKX: "XK"
    }

    return countryISOMapping[countryCode]
    }  

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }
}
